import Tone from 'tone';
import Effects from 'effects/Effects.js';
import {AbstractParameter } from 'parameters/AbstractParameter.js';
import {AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {FAST_UPDATE_RATE} from 'constants/eq8-update-rates.js';

const PEAKING = 'peaking';
const STANDARD_BANDWIDTH = 200;

class EQ8 extends Effects.Effect {
    constructor(sequencerEngine, useAnalyser=true) {
        super("EQ8", () => new Tone.Gain(1), sequencerEngine);
        this.store = sequencerEngine.store;
        this.equalizerPoints = [
            new EQPoint(sequencerEngine, this, 80, 0, 1, 1, true, 'lowshelf'),
            new EQPoint(sequencerEngine, this, 300, 0, 1, 2, false),
            new EQPoint(sequencerEngine, this, 500, 0, 1, 3, false),
            new EQPoint(sequencerEngine, this, 800, 0, 1, 4, false),
            new EQPoint(sequencerEngine, this, 1000, 0, 1, 5, ),
            new EQPoint(sequencerEngine, this, 2000, 0, 1, 6, false),
            new EQPoint(sequencerEngine, this, 4000, 0, 1, 7),
            new EQPoint(sequencerEngine, this, 8000, 0, 1, 8, true, 'highshelf')];

        this.updateRate = FAST_UPDATE_RATE;
        if (useAnalyser) {
            this.analyser = new Tone.Analyser('fft', 256);
        }
        this.outputNode = new Tone.Gain(1);

        this.setupEqualizer();

        for (let i in this.equalizerPoints) {
            this.parameters = this.parameters.concat(
                this.equalizerPoints[i].parameters);
        }
        this.updateInitialValues();
    }

    dispose() {
        super.dispose();

        if (this.analyser) {
            this.analyser.dispose();
            this.analyser = undefined;
        }

        this.equalizerPoints.forEach(p => p.dispose());
        this.equalizerPoints = undefined;
        this.outputNode.dispose();
        this.outputNode = undefined;
    }

    disconnectAllFilters() {
        this.effectNode.disconnect();
        this.equalizerPoints.filter(eq => eq.enabled)
            .forEach(eq => eq.filter.disconnect());
    }

    setupEqualizer() {
        let chainingFilters = this.equalizerPoints
            .filter(eq => eq.enabled)
            .map(eq => eq.filter);
        chainingFilters.push(this.outputNode);
        this.effectNode.chain(... chainingFilters);
    }

    getSpectrum() {
        return this.analyser.getValue();
    }

    connect(node) {
        this.outputNode.connect(node);
        if (this.analyser) {
            this.outputNode.connect(this.analyser);
        }
    }

    disconnect(node) {
        this.outputNode.disconnect(node);
        if (this.analyser) {
            this.analyser.disconnect();
        }
    }

    getJson() {
        let json = super.getJson();
        json.updateRate = this.updateRate;
        json.equalizerPoints = this.equalizerPoints.map(
            eq => eq.getJson());
        return json;
    }

    fromJson(json, isCopy) {
        super.fromJson(json, isCopy);
        if ('updateRate' in json) {
            this.updateRate = json.updateRate;
        }

        for (let i in this.equalizerPoints) {
            this.equalizerPoints[i].fromJson(json.equalizerPoints[i]);
        }

        return this;
    }

    getEffectCopy(sequencerEngine=this.sequencerEngine, ignoreId=true) {
        let copy = new EQ8(sequencerEngine);
        copy.fromJson(this.getJson(), ignoreId);
        return copy;
    }
}

class EQPoint {
    constructor(sequencerEngine, eq, frequency, gain, q, number, enabled=true, type=PEAKING) {
        this.eq = eq;
        this.name = 'EQ Frequency ' + number;
        this.number = number;
        this.type = type;
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;

        this.filter = new Tone.Filter(frequency, type);
        this.filter.Q.value = q;
        this.filter.gain.value = gain;

        this.frequencyParameter = new AudioParamValueParameter(
            this.name + ' frequency', this.filter.frequency,
            {
                min: 0,
                max: 10000,
                unit: 'hz',
                defaultValue: frequency
            });
        this.gainParameter = new AudioParamValueParameter(
            this.name + ' gain', this.filter.gain,
            {
                min: -16,
                max: 16,
                unit: 'db',
                defaultValue: gain
            });
        this.qParameter = new AudioParamValueParameter(
            this.name + ' q', this.filter.Q,
            {
                min: 0,
                max: 16,
                unit: 'Q',
                defaultValue: q
            });

        this.enabledParameter = new AbstractParameter(
            this.name + ' enabled', this,
            (o, x) => {},
            {
                min: 0,
                max: 1,
                unit: 'boolean',
                defaultValue: enabled
            });

        this.parameters = [];

        this.registerParameter(this.enabledParameter);
        this.registerParameter(this.frequencyParameter);
        this.registerParameter(this.gainParameter);
        this.registerParameter(this.qParameter);
    }

    dispose() {
        if (this.filter._param) {
            this.filter.dispose();
        }
        this.filter = undefined;
        this.parameters.forEach(x => x.dispose());
        this.gainParameter = undefined;
        this.frequencyParameter = undefined;
        this.qParameter = undefined;
        this.sequencerEngine = undefined;
        this.eq = undefined;
        this.name = undefined;
        this.store = undefined;
        this.parameters = undefined;
        this.type = undefined;
    }

    getJson() {
        let json = {
            number: this.number,
            frequency: this.frequency,
            gain: this.gain,
            q: this.q,
            enabled: this.enabled
        };

        json = saveParameterToJson(json, 'frequencyParameter', this.frequencyParameter);
        json = saveParameterToJson(json, 'qParameter', this.qParameter);
        json = saveParameterToJson(json, 'gainParameter', this.gainParameter);
        json = saveParameterToJson(json, 'enabledParameter', this.enabledParameter);

        return json;
    }

    fromJson(json) {
        this.enabled = json.enabled;
        this.frequency = json.frequency;
        this.frequencyParameter.unlockedValue = json.frequency;
        this.q = json.q;
        this.qParameter.unlockedValue = json.q;
        this.gain = json.gain;
        this.gainParameter.unlockedValue = json.gain;

        loadParameterFromJson(
            json, 'frequencyParameter', this.frequencyParameter,
            this.sequencerEngine);
        loadParameterFromJson(
            json,
            'qParameter',
            this.qParameter,
            this.sequencerEngine);
        loadParameterFromJson(
            json,
            'gainParameter',
            this.gainParameter,
            this.sequencerEngine);
        loadParameterFromJson(json, 'enabledParameter', this.enabledParameter);
    }

    registerParameter(parameter) {
        if (parameter !== undefined) {
            parameter.name = parameter.name;
            parameter.effectName = this.name;
            parameter.setStore(this.store);
            this.parameters.push(parameter);
        }
    }

    get enabled() {
        return this.enabledParameter.get();
    }

    get frequency() {
        return this.frequencyParameter.get();
    }

    get gain() {
        return this.gainParameter.get();
    }

    get q() {
        return this.qParameter.get();
    }

    set enabled(x) {
        return this.enabledParameter.set(x);
    }

    set frequency(f) {
        this.frequencyParameter.set(f);
    }

    set gain(g) {
        this.gainParameter.set(g);
    }

    set q(q) {
        this.qParameter.set(q);
    }

    frequencyToGain(frequency) {
        if (this.type === 'peaking') {
            return this.frequencyToGainPeaking(frequency);
        } else if (this.type === 'highshelf') {
            return this.frequencyToGainHighShelf(frequency);
        }  else {
            return this.frequencyToGainLowShelf(frequency);
        }
    }

    frequencyToGainPeaking(frequency) {
        let bandwidth = STANDARD_BANDWIDTH / this.q;

        let q1 = frequency - bandwidth / 2;
        let q2 = frequency + bandwidth / 2;

        if (frequency <= q1 ||
            frequency >= q2) {
            return 0;
        }

        if (frequency < this.frequency) {
            return this.gain * ((frequency - q1) / (this.frequency - q1));
        }

        return this.gain * ((q2 - frequency) / (q2 - this.frequency));
    }

    frequencyToGainHighShelf(frequency) {
        let q1 = frequency - STANDARD_BANDWIDTH / 2;
        frequency += STANDARD_BANDWIDTH / 2;
        if (frequency <= q1) {
            return 0;
        } else if (frequency >= this.frequency) {
            return this.gain;
        } else {
            return this.gain * ((frequency - q1) / (this.frequency - q1));
        }
    }

    frequencyToGainLowShelf(frequency) {
        let q1 = frequency + STANDARD_BANDWIDTH / 2;
        if (frequency >= q1) {
            return 0;
        } else if (frequency <= this.frequency) {
            return this.gain;
        } else {
            return this.gain * ((frequency - q1) / (this.frequency - q1));
        }
    }
}

export default EQ8;
